'use client'

import { useEffect, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useCartStore, CartItem } from '@/store/cart-store';
import { cn } from '@/lib/utils';
import { ShoppingBag, ChevronUp, ChevronDown, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { CartButton } from '@/components/cart/cart-button';
import { Button } from '../ui/button';
import { useRestaurantStore } from "@/store/restaurant-store";
import { useUserStore } from "@/store/user-store";

// Define the props if needed, though this component seems self-contained with the store
// interface CartFooterProps {}

export function CartFooter(/* props: CartFooterProps */) {
  const pathname = usePathname();
  const router = useRouter();
  
  // Don't render the cart footer on the cart page
  if (pathname === '/cart') {
    return null;
  }

  const cartStore = useCartStore();
  const [showCart, setShowCart] = useState(false);
  const [isCartExpanded, setIsCartExpanded] = useState(false);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const restaurantStore = useRestaurantStore();
  const isSheetOpen = useUserStore((state) => {
    console.log("CartFooter - isSheetOpen:", state.isSheetOpen);
    return state.isSheetOpen;
  });

  // Initialize cart state and subscribe to changes
  useEffect(() => {
    // Ensure cartStore is initialized before accessing getState
    if (!cartStore) return;
    
    const initialItems = cartStore.getState()?.items || [];
    setCartItems(initialItems);
    setShowCart(initialItems.length > 0);

    const unsubscribe = cartStore.subscribe((state: any) => {
      setCartItems(state.items);
      setShowCart(state.items.length > 0);
      // Optional: Keep console log for debugging if needed
      // console.log('Cart Updated (Footer):', {
      //   totalItems: state.items.length,
      //   totalAmount: state.items.reduce((acc: number, item: CartItem) => acc + (item.price * item.quantity), 0),
      //   items: state.items
      // });
    });

    return () => unsubscribe?.();
  }, [cartStore]); // Dependency array includes cartStore

  // Reset cart expansion state on mount (optional, depends on desired behavior)
   useEffect(() => {
     setIsCartExpanded(false);
   }, []);

  const getTotalItems = () => {
    return cartItems.reduce((acc: number, item: CartItem) => acc + item.quantity, 0);
  };

  const getTotalPrice = () => {
    // Ensure price and quantity are numbers before calculation
    return cartItems.reduce((acc: number, item: CartItem) => {
      const price = Number(item.price) || 0;
      const quantity = Number(item.quantity) || 0;
      return acc + (price * quantity);
    }, 0);
  };

  const handleClearCart = () => {
    // Ensure cartStore is initialized
    if (!cartStore) return;

    toast.warning("Are you sure you want to clear your cart?", {
      action: {
        label: "Yes, Clear",
        onClick: () => {
          cartStore.getState().clearCart();
          setIsCartExpanded(false); // Collapse cart after clearing
          toast.success("Cart cleared successfully");
        }
      },
      cancel: {
        label: "Cancel",
        onClick: () => {} // Do nothing on cancel
      },
      duration: 5000,
    });
  };

  const checkMinimumOrderAmount = () => {
    // Find first item with restaurant_info to get minimum order amount
    const itemWithRestaurantInfo = cartItems.find(item => item.restaurant_info);
    if (!itemWithRestaurantInfo?.restaurant_info) return null;
    const minimumOrder = itemWithRestaurantInfo.restaurant_info.merchantMinimumOrder;
    const totalAmount = getTotalPrice();
    
    if (minimumOrder && totalAmount < minimumOrder) {
      return {
        isValid: false,
        minimumOrder,
        difference: minimumOrder - totalAmount
      };
    }
    
    return { isValid: true, minimumOrder };
  };

  const handleCheckout = () => {
    const orderCheck = checkMinimumOrderAmount();
    
    if (!orderCheck?.isValid) {
      toast.error(
        <div className="flex flex-col gap-1">
          <p className="font-medium">Minimum order amount not met</p>
          <p className="text-sm text-gray-500">
            Add ₹{orderCheck?.difference?.toFixed(2)} more to reach minimum order amount of ₹{orderCheck?.minimumOrder}
          </p>
        </div>
      );
      return;
    }

    // Get first cart item's restaurant info and set delivery/preparation times
    const firstItem = cartItems[0];
    if (firstItem?.restaurant_info) {
      restaurantStore.setDeliveryTime(firstItem.restaurant_info.delivery_time || 30);
      restaurantStore.setOrderPreparationTime(firstItem.restaurant_info.order_preparation_time || 0);
    }
    
    router.push('/cart');
  };

  // Early return if cart is empty or not initialized yet
  if (!showCart || !cartStore) {
    return null;
  }

  return (
    <>
      {/* Desktop Cart - Fixed on right side */}
      <div className="hidden md:block fixed right-4 bottom-4 z-50">
        <div
          className={cn(
            "bg-white rounded-xl shadow-xl border transition-all duration-300 ease-out transform",
            isCartExpanded ? "w-[400px] scale-100" : "w-[320px] scale-95",
            "hover:shadow-2xl origin-bottom-right"
          )}
        >
          {/* Cart Header */}
          <div
            onClick={() => setIsCartExpanded(!isCartExpanded)}
            className="p-4 flex items-center gap-3 cursor-pointer hover:bg-gray-50/80 transition-colors rounded-t-xl" // Added rounded-t-xl
          >
            <div className="bg-[#900f21]/10 p-2.5 rounded-xl">
              <ShoppingBag className="h-5 w-5 text-[#900f21]" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-medium">{getTotalItems()} Items</span>
                <span className="text-gray-500">•</span>
                <span className="font-medium">₹{getTotalPrice().toFixed(2)}</span> {/* Format price */}
              </div>
              <p className="text-xs text-gray-500">Click to {isCartExpanded ? 'collapse' : 'expand'}</p>
            </div>
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 transition-transform duration-300"> {/* Changed bg */}
              {isCartExpanded ?
                <ChevronDown className="h-5 w-5 text-gray-600" /> :
                <ChevronUp className="h-5 w-5 text-gray-600" />
              }
            </div>
          </div>

          {/* Cart Items Preview */}
          <div
            className={cn(
              "flex flex-col overflow-hidden", // Added overflow-hidden
              "transition-all duration-300 ease-out",
              isCartExpanded ? "max-h-[450px] opacity-100" : "max-h-0 opacity-0", // Simplified animation
            )}
          >
            <div className="bg-gray-50 border-y sticky top-0 z-10"> {/* Changed bg */}
              <div className="px-4 py-2 flex justify-between items-center"> {/* Adjusted padding */}
                <h3 className="font-semibold text-sm text-gray-700">Your Cart</h3> {/* Changed text */}
                <button
                  onClick={handleClearCart}
                  className="text-xs text-red-600 hover:text-red-700 font-medium flex items-center gap-1" // Added icon
                >
                   <Trash2 className="h-3 w-3" /> Clear All
                </button>
              </div>
            </div>

            <div className="overflow-y-auto flex-1 max-h-[300px] scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"> {/* Adjusted scrollbar */}
             {cartItems.length > 0 ? (
                cartItems.map((item: CartItem) => (
                  <div key={item.id} className="p-3 flex items-center justify-between hover:bg-gray-50/50 transition-colors border-b last:border-b-0"> {/* Adjusted padding & border */}
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <img src={item.image} alt={item.name} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" /> {/* Adjusted size */}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{item.name}</p>
                        <p className="text-xs text-gray-600">₹{Number(item.price || 0).toFixed(2)} × {item.quantity}</p> {/* Format price */}
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <CartButton
                        product={item}
                        variant="outline"
                        size="sm"
                      />
                    </div>
                  </div>
                ))
             ) : (
                 <p className="text-center text-gray-500 py-6 text-sm">Your cart is empty.</p>
             )}
            </div>

            {cartItems.length > 0 && ( // Only show checkout if items exist
              <div className="p-3 bg-white border-t sticky bottom-0 z-10 rounded-b-xl"> {/* Adjusted padding & added rounded-b-xl */}
                <button
                  onClick={handleCheckout}
                  className="w-full bg-[#900f21] text-white py-2.5 rounded-lg font-semibold hover:bg-[#a01122] transition-colors text-sm" // Adjusted styles
                >
                  Checkout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>


      {/* Mobile Cart - Fixed at bottom */}
      <div className={cn(
        "block md:hidden fixed inset-0 z-[100] pointer-events-none",
        isSheetOpen ? "hidden" : ""
      )}>
        {/* Backdrop */}
        <div
          className={cn(
            "absolute inset-0",
            isCartExpanded ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
          )}
          onClick={() => setIsCartExpanded(false)}
        />

        <div
          className={cn(
            "fixed left-0 right-0 bottom-0 bg-white rounded-t-2xl shadow-lg pointer-events-auto transition-transform duration-300 ease-out mb-16",
            isCartExpanded ? "translate-y-0" : "translate-y-[calc(100%-76px)]" // Adjusted height for bottom bar (py-3 + text = ~76px)
          )}
          style={{
            maxHeight: isCartExpanded ? '75vh' : '76px', // Max height when expanded, fixed height when collapsed
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Cart Header when expanded */}
          {isCartExpanded && (
            <div className="px-4 py-3 border-b bg-white sticky top-0 z-20 flex-shrink-0 rounded-t-2xl border-t border-gray-300"> {/* Added rounded-t */}
              {/* Drag Handle */}
              <div
                className="w-10 h-1 bg-gray-300 rounded-full mx-auto mb-3 cursor-grab active:cursor-grabbing" // Adjusted size
                onClick={() => setIsCartExpanded(false)} // Allow collapsing by clicking handle
              />
              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-lg">Your Cart</h3>
                <button
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent collapsing when clicking clear
                    handleClearCart();
                  }}
                  className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1 p-1 -m-1" // Added padding for better click area
                >
                  <Trash2 className="h-4 w-4" />
                  Clear Cart
                </button>
              </div>
            </div>
          )}

          {/* Scrollable Cart Items */}
          {isCartExpanded && (
            <div className="overflow-y-auto flex-1 bg-gray-50">
              <div className="p-3 space-y-3"> {/* Adjusted padding */}
                {cartItems.length > 0 ? (
                  cartItems.map((item: CartItem) => (
                    <div key={item.id} className="bg-white rounded-xl p-3 shadow-sm">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 flex-1 min-w-0"> {/* Ensure text wraps */}
                          <img src={item.image} alt={item.name} className="w-14 h-14 rounded-lg object-cover flex-shrink-0" /> {/* Adjusted size */}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{item.name}</p> {/* Truncate long names */}
                            <p className="text-xs text-gray-600">₹{Number(item.price || 0).toFixed(2)} × {item.quantity}</p> {/* Format price */}
                          </div>
                        </div>
                        <div className="flex-shrink-0">
                          <CartButton
                            product={item}
                            variant="outline"
                            size="sm"
                          />
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-gray-500 py-10 text-sm">Your cart is empty.</p> // Adjusted padding
                )}
              </div>
            </div>
          )}

          {/* Fixed Bottom Bar (Always Visible) */}
          <div className={cn(
              "bg-[#900f21] border-t px-4 py-3 text-white flex-shrink-0",
              isCartExpanded ? "rounded-b-none" : "rounded-t-2xl" // Apply rounding only when collapsed
            )}
            >
            {/* Drag Handle Area (only visible when collapsed) */}
            {!isCartExpanded && (
               <div
                className="w-10 h-1 bg-gray-400 rounded-full mx-auto mb-2 cursor-grab active:cursor-grabbing" // Adjusted size
                onClick={() => setIsCartExpanded(true)} // Expand on click
              />
            )}
            <div
              className="flex items-center justify-between"
              onClick={() => !isCartExpanded && setIsCartExpanded(true)} // Expand on click anywhere on the bar
            >
              <div className="flex items-center gap-3">
                <div className="relative">
                   <ShoppingBag className="h-6 w-6 text-white" />
                   {getTotalItems() > 0 && (
                     <span className="absolute -top-1.5 -right-1.5 bg-white text-[#900f21] text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center leading-none"> {/* Adjusted position & size */}
                       {getTotalItems()}
                     </span>
                   )}
                </div>
                <div>
                  <span className="font-semibold text-base">₹{getTotalPrice().toFixed(2)}</span> {/* Format price */}
                  {/* Optional: Add item count back if needed */}
                  {/* <p className="text-xs text-gray-200">{getTotalItems()} Items</p> */}
                </div>
              </div>
              <div className='flex items-center gap-3 justify-center'>
                <button
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent expansion toggle
                    const orderCheck = checkMinimumOrderAmount();
                    const firstItem = cartItems[0];
                    if (firstItem?.restaurant_info) {
                      restaurantStore.setDeliveryTime(firstItem.restaurant_info.delivery_time || 30);
                      restaurantStore.setOrderPreparationTime(firstItem.restaurant_info.order_preparation_time || 0);
                    }
                    if (!orderCheck?.isValid) {
                      toast.error(
                        <div className="flex flex-col gap-1">
                          <p className="font-medium">Minimum order amount not met</p>
                          <p className="text-sm text-gray-500">
                            Add ₹{orderCheck?.difference?.toFixed(2)} more to reach minimum order amount of ₹{orderCheck?.minimumOrder}
                          </p>
                        </div>
                      );
                      return;
                    }
                    router.push('/cart');
                  }}
                  className="text-white rounded-lg font-semibold text-sm transition-colors"
                >
                  Checkout
                </button>
                <span> | </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClearCart();
                  }}
                  className="bg-transparent h-8 w-8 rounded-lg font-semibold text-sm transition-colors"
                >
                  <Trash2/>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
